import { HrefLink as Link } from "@/components/ui/href-link";
import { buttonVariants } from "@/components/ui/button";
import { RequirePermission } from "@/hooks/shared/CheckAuth";
import { createFileRoute } from "@tanstack/react-router";

import { EmptyState } from "@/components/dashboard/data-table/empty-state";
import { FilterBar } from "@/components/dashboard/data-table/filter-bar";
import { Pagination } from "@/components/dashboard/data-table/pagination";
import { SortHeader } from "@/components/dashboard/data-table/sort-header";
import {
  TBody,
  TD,
  TEmpty,
  TH,
  THead,
  TR,
  Table,
  TableShell,
} from "@/components/dashboard/data-table/table";
import { PersonAvatar } from "@/components/employees/person-avatar";
import { PageHeader } from "@/components/dashboard/page-header";
import { QueryState } from "@/components/dashboard/query-state";
import { StatusPill, type StatusTone } from "@/components/dashboard/status-pill";
import { filters, incidents as copy, nav } from "@/content";
import {
  buildQuery,
  readEnum,
  readNumber,
  readPage,
  readPageSize,
  readSearch,
  readSort,
  emptyPage,
} from "@/utils/query-params";
import {
  INCIDENT_SORT_COLUMNS,
  INCIDENT_STATUSES,
  incidentStatusCounts,
  listIncidentGuards,
  listIncidentReports,
  listIncidentTypes,
  type IncidentStatus,
} from "@/api/incidents";
import { listZones } from "@/api/zones";
import { instantAt, isValidDateKey } from "@/utils/time";
import { cn } from "@/utils";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useQueryParams } from "@/hooks/shared/useQueryParams";
import { useFmt } from "@/hooks/shared/useFmt";
import { useDocumentTitle } from "@/hooks/shared/useDocumentTitle";
import { useSession } from "@/context/UserContext";

const BASE_PATH = "/dashboard/incidents";

const STATUS_TONE: Record<IncidentStatus, StatusTone> = {
  new: "attention",
  under_review: "muted",
  resolved: "positive",
  rejected: "negative",
};

const STATUS_LABEL: Record<IncidentStatus, string> = {
  new: copy.statusNew,
  under_review: copy.statusUnderReview,
  resolved: copy.statusResolved,
  rejected: copy.statusRejected,
};

export const Route = createFileRoute("/_main/dashboard/incidents/")({
  component: () => (
    <RequirePermission action="incidents.read">
      <IncidentsPage />
    </RequirePermission>
  ),
});

function IncidentsPage() {
  useDocumentTitle(copy.title);
  const { user } = useSession();
  const params = useQueryParams();
  const fmt = useFmt();

  const sort = readSort(params, INCIDENT_SORT_COLUMNS, "createdAt");
  const activeStatus = readEnum(params, "status", INCIDENT_STATUSES);

  const rawFrom = typeof params.from === "string" ? params.from : undefined;
  const rawTo = typeof params.to === "string" ? params.to : undefined;
  const appliedFrom = rawFrom && isValidDateKey(rawFrom) ? rawFrom : undefined;
  const appliedTo = rawTo && isValidDateKey(rawTo) ? rawTo : undefined;

  const query = {
    search: readSearch(params),
    status: activeStatus,
    reportTypeId: readNumber(params, "reportType"),
    guardId: readNumber(params, "guard"),
    zoneId: readNumber(params, "zone"),
    from: appliedFrom,
    to: appliedTo,
    sort: { column: sort.column, direction: sort.direction },
    page: readPage(params),
    pageSize: readPageSize(params),
  };

  const list = useQuery({
    queryKey: ["incidents", "list", query],
    queryFn: () => listIncidentReports(query),
    placeholderData: keepPreviousData,
  });

  const countsQuery = useQuery({
    queryKey: ["incidents", "counts"],
    queryFn: incidentStatusCounts,
  });

  const typesQuery = useQuery({
    queryKey: ["incidentTypes", "list"],
    queryFn: listIncidentTypes,
  });

  const guardsQuery = useQuery({
    queryKey: ["incidents", "guards"],
    queryFn: listIncidentGuards,
  });

  const zonesQuery = useQuery({
    queryKey: ["zones", "options", "incidents"],
    queryFn: () =>
      listZones({
        search: "",
        state: "live",
        sort: { column: "name", direction: "asc" },
        page: 1,
        pageSize: 200,
      }),
  });

  const result = list.data ?? emptyPage<never>();
  const counts = countsQuery.data ?? { new: 0, under_review: 0, resolved: 0, rejected: 0 };
  const allCount =
    counts.new + counts.under_review + counts.resolved + counts.rejected;
  const isFiltered =
    Boolean(readSearch(params)) ||
    Boolean(query.reportTypeId) ||
    Boolean(query.guardId) ||
    Boolean(query.zoneId) ||
    Boolean(appliedFrom) ||
    Boolean(appliedTo);

  const tabs = [
    { value: undefined, label: copy.tabAll, count: allCount },
    { value: "new" as const, label: copy.tabNew, count: counts.new },
    {
      value: "under_review" as const,
      label: copy.tabUnderReview,
      count: counts.under_review,
    },
    { value: "resolved" as const, label: copy.tabResolved, count: counts.resolved },
    { value: "rejected" as const, label: copy.tabRejected, count: counts.rejected },
  ];

  return (
    <div className="mx-auto w-full max-w-console px-6 py-8">
      <PageHeader eyebrow={nav.groups.operations} title={copy.title} lead={copy.lead} />

      <div className="mt-6 flex flex-wrap gap-1 border-b border-n3">
        {tabs.map((tab) => {
          const href = `${BASE_PATH}${buildQuery(params, { status: tab.value, page: 1 })}`;
          const selected = activeStatus === tab.value;
          return (
            <Link
              key={tab.label}
              to={href}
              aria-current={selected ? "page" : undefined}
              className={cn(
                "-mb-px border-b-2 px-3 py-2.5 text-xs font-semibold transition-colors duration-200 ease-brand",
                selected ? "border-orange text-n9" : "border-transparent text-n6 hover:text-n8",
              )}
            >
              {tab.label}
              <span className="ms-1.5 tabular text-n6">{fmt.count(tab.count)}</span>
            </Link>
          );
        })}
      </div>

      <div className="mt-5">
        <FilterBar
          params={params}
          basePath={BASE_PATH}
          searchPlaceholder={copy.searchPlaceholder}
          selects={[
            {
              key: "reportType",
              label: copy.filterType,
              value: query.reportTypeId ? String(query.reportTypeId) : "",
              options: (typesQuery.data ?? [])
                .filter((row) => row.active)
                .map((row) => ({ value: String(row.id), label: row.nameAr })),
            },
            {
              key: "guard",
              label: copy.filterGuard,
              value: query.guardId ? String(query.guardId) : "",
              options: (guardsQuery.data ?? []).map((row) => ({
                value: String(row.id),
                label: row.name,
              })),
            },
            {
              key: "zone",
              label: copy.filterZone,
              value: query.zoneId ? String(query.zoneId) : "",
              options: (zonesQuery.data?.rows ?? []).map((row) => ({
                value: String(row.id),
                label: row.name,
              })),
            },
          ]}
        />
      </div>

      <form action={BASE_PATH} method="get" className="mt-4 flex flex-wrap items-end gap-3">
        {Object.entries(params).map(([key, value]) =>
          key === "from" || key === "to" || typeof value !== "string" ? null : (
            <input key={key} type="hidden" name={key} value={value} />
          ),
        )}
        <label className="flex flex-col gap-1.5">
          <span className="text-[11px] font-semibold text-n6">{copy.filterFrom}</span>
          <input
            type="date"
            name="from"
            defaultValue={typeof params.from === "string" ? params.from : ""}
            className="tabular h-10 rounded-lg border border-n4 bg-n0 px-3 text-sm text-n8 outline-none transition-colors focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50"
          />
        </label>
        <label className="flex flex-col gap-1.5">
          <span className="text-[11px] font-semibold text-n6">{copy.filterTo}</span>
          <input
            type="date"
            name="to"
            defaultValue={typeof params.to === "string" ? params.to : ""}
            className="tabular h-10 rounded-lg border border-n4 bg-n0 px-3 text-sm text-n8 outline-none transition-colors focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50"
          />
        </label>
        <button type="submit" className={buttonVariants({ variant: "outline", size: "xl" })}>
          {filters.apply}
        </button>
        <p className="w-full text-[11px] text-n6">{copy.dateRangeHint}</p>
        {appliedFrom || appliedTo ? (
          <p className="tabular w-full text-[11px] font-semibold text-n7">
            {copy.dateRangeApplied}{" "}
            {appliedFrom ? fmt.dateNumeric(instantAt(appliedFrom, "12:00")) : "…"} –{" "}
            {appliedTo ? fmt.dateNumeric(instantAt(appliedTo, "12:00")) : "…"}
          </p>
        ) : null}
      </form>

      <QueryState isPending={list.isPending} isError={list.isError && !list.data} error={list.error}>
        <TableShell className="mt-5" busy={list.isPlaceholderData}>
          <Table>
            <THead>
              <SortHeader
                column="guard"
                label={copy.columnGuard}
                params={params}
                basePath={BASE_PATH}
                current={sort}
              />
              <SortHeader
                column="reportType"
                label={copy.columnType}
                params={params}
                basePath={BASE_PATH}
                current={sort}
              />
              <TH>{copy.columnArea}</TH>
              <SortHeader
                column="status"
                label={copy.columnStatus}
                params={params}
                basePath={BASE_PATH}
                current={sort}
              />
              <SortHeader
                column="createdAt"
                label={copy.columnSubmitted}
                params={params}
                basePath={BASE_PATH}
                current={sort}
              />
              <TH>{copy.columnAttachments}</TH>
            </THead>
            <TBody>
              {result.rows.length === 0 ? (
                <TEmpty colSpan={6}>
                  <EmptyState
                    variant={isFiltered || activeStatus ? "filtered" : "empty"}
                    title={
                      isFiltered || activeStatus ? copy.emptyFilteredTitle : copy.emptyTitle
                    }
                    lead={
                      isFiltered || activeStatus ? copy.emptyFilteredLead : copy.emptyLead
                    }
                    actionHref={isFiltered || activeStatus ? BASE_PATH : undefined}
                    actionLabel={isFiltered || activeStatus ? copy.clearFilters : undefined}
                  />
                </TEmpty>
              ) : (
                result.rows.map((row) => (
                  <TR key={row.id}>
                    <TD>
                      <div className="flex items-center gap-2.5">
                        <PersonAvatar name={row.guardName} photoUrl={row.photoUrl} />
                        <Link
                          to={`${BASE_PATH}/${row.id}`}
                          className="font-semibold text-n8 hover:text-brand"
                        >
                          {row.guardName}
                        </Link>
                      </div>
                    </TD>
                    <TD>
                      <span className="flex items-center gap-2">
                        <span
                          aria-hidden
                          className="size-2 shrink-0 rounded-sm"
                          style={{
                            backgroundColor: row.reportType.color ?? "var(--orange)",
                          }}
                        />
                        {row.reportType.nameAr}
                        {row.otherLabel ? (
                          <span className="text-2xs text-n6">({row.otherLabel})</span>
                        ) : null}
                      </span>
                    </TD>
                    <TD>{row.zoneName ?? "—"}</TD>
                    <TD>
                      <StatusPill tone={STATUS_TONE[row.status]}>
                        {STATUS_LABEL[row.status]}
                      </StatusPill>
                    </TD>
                    <TD className="whitespace-nowrap text-n7">{fmt.dateTime(row.createdAt)}</TD>
                    <TD numeric>{row.attachmentCount}</TD>
                  </TR>
                ))
              )}
            </TBody>
          </Table>

          {result.rows.length > 0 ? (
            <Pagination
              page={result.page}
              pageCount={result.pageCount}
              pageSize={result.pageSize}
              total={result.total}
              params={params}
              basePath={BASE_PATH}
              numerals={user.numeralSystem}
            />
          ) : null}
        </TableShell>
      </QueryState>
    </div>
  );
}
